import {
  HealthcareManagementPageMetricsDocument,
  HealthcareManagementPagePlanDetailsDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

interface UseHealthcarePlanMetricsOptions {
  companyId: string
  selectedPlanId?: string
}

/**
 * Custom hook to fetch and calculate healthcare plan metrics.
 */
export function useHealthcarePlanMetrics({
  companyId,
  selectedPlanId,
}: UseHealthcarePlanMetricsOptions) {
  // Fetch plan details (plans + total employees)
  const { data: planData, loading: planLoading } = useQuery(
    HealthcareManagementPagePlanDetailsDocument,
    {
      variables: { companyId },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: false,
    }
  )

  // Fetch metrics using filtered queries (no pagination, server-side filtering)
  const { data: metricsData, loading: metricsLoading } = useQuery(
    HealthcareManagementPageMetricsDocument,
    {
      variables: { companyId },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: false,
    }
  )

  const loading = planLoading || metricsLoading

  // Extract plan data
  const healthcarePlans = planData?.benefits?.healthcarePlansConnection?.nodes ?? []
  const totalEmployees = planData?.employees?.employeesConnection?.totalCount ?? 0

  const selectedPlan =
    healthcarePlans.find((plan) => plan.id === selectedPlanId) ?? healthcarePlans[0]

  // Extract metrics from filtered queries
  const employeeEnrollmentsCount = metricsData?.benefits?.activeEmployeeEnrollments?.totalCount ?? 0
  const dependentEnrollmentsCount =
    metricsData?.benefits?.activeDependentEnrollments?.totalCount ?? 0

  // Calculate unique employees from minimal employeeId data
  const uniqueEmployees = metricsData?.benefits?.activeEmployeeEnrollments?.totalCount ?? 0

  // Calculate costs (in cents)
  const totalCostCents =
    employeeEnrollmentsCount * (selectedPlan?.employeeCostCents ?? 0) +
    dependentEnrollmentsCount * (selectedPlan?.dependentCostCents ?? 0)

  const averageCostPerEmployeeCents = uniqueEmployees
    ? Math.round(totalCostCents / uniqueEmployees)
    : 0

  // Adoption rate: active enrolled employees / total employees
  const adoptionRate = totalEmployees ? (uniqueEmployees / totalEmployees) * 100 : 0

  return {
    // Plan data
    selectedPlan,
    healthcarePlans,
    planDocumentUrl: selectedPlan?.planDocument?.url,

    // Metrics
    uniqueEmployees,
    dependentEnrollmentsCount,
    totalEmployees,
    totalCostCents,
    averageCostPerEmployeeCents,
    adoptionRate,

    loading,
  }
}
